import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { AuthService } from 'src/core/services/auth.service';
import { BookmarkService } from 'src/core/services/bookmark.service';
import { LoadingService } from 'src/core/services/loading.service';
import { ModalService } from 'src/core/services/modal.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { SelectedSiteService } from 'src/core/services/selectedsite.service';
import { WidgetService } from 'src/core/services/widget.service';
import { SelectedSite } from 'src/core/types/selectedSite.types';
import { SelectedSystem, SystemIBase } from 'src/core/types/selectedSystem.types';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-header-widget',
  imports: [CommonModule, TranslocoModule],
  templateUrl: './header-widget.component.html',
  styleUrl: './header-widget.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class HeaderWidgetComponent {
  @Input() restrictedView = false;

  constructor(
    private loadingService: LoadingService,
    private selectedContentService: SelectedContentService,
    private selectedSystemService: SelectedsystemService,
    private selectedSiteService: SelectedSiteService,
    private widgetService: WidgetService,
    private authService: AuthService,
    private bookmarkService: BookmarkService,
    private modalService: ModalService,
    private translocoService: TranslocoService,
  ) { }

  deviceInformation: SystemIBase;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  isBookmarked: boolean = false;
  updatingBookmark: boolean = true;
  errorBookmark: boolean = false;
  selectedSystem: SelectedSystem;
  selectedSite: SelectedSite;
  businessPartnerMode = environment.businessPartnerMode;

  //Animations
  dots: string = '';
  intervalId: any;

  @ViewChild('systemicon') systemicon: ElementRef;
  @ViewChild('systemmenu') systemmenu: ElementRef;
  systemsOpen: boolean = false;

  get isSiteSelection(): boolean {
    return this.selectedContentService.selection.kind === 'site';
  }

  get hasSelection(): boolean {
    return this.isSiteSelection ? Boolean(this.selectedSite?.Id) : Boolean(this.selectedSystem?.SerialNumber);
  }

  get headerIcon(): string {
    return this.isSiteSelection ? 'manufacturer' : 'laboratory-system';
  }

  get headerHelpTopic(): string {
    return this.isSiteSelection ? 'Site' : 'Device';
  }

  get headerTitle(): string {
    if (this.isLoading && !this.isError) {
      return this.isSiteSelection ? `${this.translocoService.translate('headerWidget.loadingSite')} ${this.dots}` : `${this.translocoService.translate('headerWidget.loadingDevice')} ${this.dots}`;
    }

    if (this.isSiteSelection) {
      return this.selectedSite?.Name || this.translocoService.translate('headerWidget.noSelection');
    }

    return this.selectedSystem?.SerialNumber || this.translocoService.translate('headerWidget.noSelection');
  }

  get headerSubtitle(): string {
    if (this.isLoading) {
      return this.isSiteSelection ? this.translocoService.translate('headerWidget.loadingSite') : this.translocoService.translate('headerWidget.loadingLocation');
    }

    if (this.isSiteSelection) {
      return this.selectedSite?.Id || this.translocoService.translate('headerWidget.selectAbove');
    }

    return this.selectedSystem?.LabName || this.translocoService.translate('headerWidget.selectAbove');
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event): void {
    this.handleSystemsClick(event);
  }

  public handleSystemsClick(event: Event) {
    if (!this.systemmenu || !this.systemicon) return;

    const clickedInside = this.systemmenu.nativeElement.contains(event.target);
    const clickedIcon = this.systemicon.nativeElement.contains(event.target);

    if (!clickedInside && !clickedIcon && this.systemsOpen) {
      this.systemsOpen = false;
      this.systemicon.nativeElement.active = false;
    }
  }


  ngOnInit(): void {
    this.loadingService.$isSystemDetailsLoading.subscribe((isSystemLoading) => {
      if (isSystemLoading) { this.startAnimation() }
      this.isLoading = isSystemLoading;
      if (!isSystemLoading) { clearInterval(this.intervalId) }
    });
    this.loadingService.$isSystemDetailsError.subscribe((isError) => {
      this.isError = isError;
    });
    this.loadingService.$isSystemDetailsEmpty.subscribe((isEmpty) => {
      this.isEmpty = isEmpty;
    });
    this.selectedSystemService.$ibaseinformation.subscribe((ibaseinformation: SystemIBase) => {
      this.deviceInformation = ibaseinformation;
    });


    this.selectedContentService.selectedSystem$
      .pipe(filter((s): s is SelectedSystem => !!s))
      .subscribe((selectedSystem) => {
        this.selectedSystem = selectedSystem;

        let body = {
          accountId: this.authService.userData.basic["id"],
          serial: selectedSystem["SerialNumber"],
          material: selectedSystem["MaterialNumber"]
        }

        this.bookmarkService.getBookmark(body).subscribe({
          next: (data: any[]) => {
            if (data.length > 0) {
              this.isBookmarked = true;
            } else {
              this.isBookmarked = false;
            }
            this.updatingBookmark = false;
          },
          error: (err) => {
            console.error("Error fetching bookmarks:", err);
            this.updatingBookmark = false;
            this.errorBookmark = true;
          }
        });

      });

    this.selectedContentService.selectedSite$
      .pipe(filter((s): s is SelectedSite => !!s))
      .subscribe((selectedSite) => {
        this.selectedSite = selectedSite;
      });

    this.bookmarkService.$bookmarks.subscribe((bookmarks) => {
      this.updatingBookmark = true;

      if (bookmarks.filter(f => f.serial + f.material === this.selectedSystem?.SerialNumber + this.selectedSystem?.MaterialNumber).length > 0) {
        this.isBookmarked = true;
      } else {
        this.isBookmarked = false;
      }

      this.updatingBookmark = false;
    });
  }

  updateBookmark(event: any) {

    event["target"].disabled = true;
    this.updatingBookmark = true;

    let body = {
      accountId: this.authService.userData.basic["id"],
      serial: this.selectedContentService.currentSystem?.SerialNumber,
      material: this.selectedContentService.currentSystem?.MaterialNumber,
    }

    this.bookmarkService.updateBookmark(body).subscribe({
      next: (data: any[]) => {
        if (data.filter((item) => item.serial === body.serial && item.material === body.material).length > 0) {
          this.isBookmarked = true;
        } else {
          this.isBookmarked = false;
        }
        event["target"].disabled = false;
        this.bookmarkService.$refreshBookmarks.next();
        this.updatingBookmark = false;
      },
      error: (err) => {
        console.error("Error updating bookmark:", err);
      }
    });
  }

  startAnimation() {
    this.dots = '';
    this.intervalId = setInterval(() => {
      this.dots += '.';
      if (this.dots.length > 3) {
        this.dots = '';
      }
    }, 500);
  }

  openHelp(section) {
    this.modalService.$helpModalChanged.next({ modal: section, open: true });
  }

  openSystem() {
    this.systemsOpen = true;
  }

  systemClosed() {
    this.systemsOpen = false;
  }

  refresh() {
    const selection = this.selectedContentService.selection;
    if (selection.kind === 'site') {
      this.selectedSiteService.$reloadSelectedSite.next();
      return;
    }

    if (selection.kind === 'device') {
      this.selectedSystemService.$reloadSelectedSystem.next();
    }
  }

  openWithParams(params: Record<string, string>) {

    if (!params || Object.keys(params).length === 0) {
      return;
    }

    // Create a URL object from the current location
    const url = new URL(window.location.href);

    // Remove all existing query params
    url.search = '';

    // Add or update params
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    // Open the new URL in a new browser tab
    window.open(url.toString(), "_blank");
  }

  onMaximizeBookmarks(filter?: string) {
    if (this.widgetService.$workareaSize.value !== "small" && this.widgetService.$selectedWidget.value === "bookmark" && !filter) {
      this.widgetService.closeWidget();
      return;
    }

    this.widgetService.expandWidget("bookmark", filter);
  }

}
